/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { getRandomId } from '../../utils/getRandomId';
import { AuthActions, AuthLogoutAction } from '../auth/types';
import {
  NotificationsActions,
  NotificationsActionTypes,
  NotificationsState,
  RemoveNotificationAction,
  SetNotificationReadAction,
} from './types';

export const initialNotificationsState: NotificationsState = {
  count: 0,
  unreadCount: 0,
  readCount: 0,
  options: [],
};

export function notifications(
  state = initialNotificationsState,
  action: NotificationsActionTypes | AuthLogoutAction,
): NotificationsState {
  switch (action.type) {
    case NotificationsActions.ADD_NOTIFICATION: {
      const newNotification = {
        ...action.payload.notification,
        id: action.payload.notification.id || getRandomId(),
      };
      const nextOptions = [newNotification].concat(state.options);
      return {
        ...state,
        options: nextOptions,
        ...calculateCounts(nextOptions),
      };
    }
    case NotificationsActions.SET_NOTIFICATIONS:
      return {
        ...state,
        options: action.payload.options,
        ...calculateCounts(action.payload.options),
      };
    case NotificationsActions.SET_NOTIFICATION_READ:
      return setNotificationRead(state, action);
    case NotificationsActions.SET_NOTIFICATIONS_READ:
      return {
        ...state,
        options: state.options.map(o => ({
          ...o,
          isRead: action.payload.isRead,
        })),
        readCount: state.options.length,
        unreadCount: 0,
      };
    case NotificationsActions.REMOVE_NOTIFICATION:
      return removeNotifications(state, action);
    case AuthActions.AUTH_LOGOUT:
      return { ...initialNotificationsState };
    default:
      return state;
  }
}

export const calculateCounts = (
  options: NotificationsState['options'],
): { count: number; unreadCount: number; readCount: number } => {
  let count = 0;
  let readCount = 0;
  let unreadCount = 0;
  options.forEach(i => {
    count++;
    i.isRead ? readCount++ : unreadCount++;
  });
  return { count, readCount, unreadCount };
};

export const setNotificationRead = (
  state: NotificationsState,
  action: SetNotificationReadAction,
): NotificationsState => {
  const { id, isRead } = action.payload;
  const nextOptions = state.options.map(i => {
    if (i.id !== id) {
      return i;
    }
    return {
      ...i,
      isRead,
    };
  });
  return {
    ...state,
    options: nextOptions,
    ...calculateCounts(nextOptions),
  };
};

export const removeNotifications = (
  state: NotificationsState,
  action: RemoveNotificationAction,
): NotificationsState => {
  const { id } = action.payload;
  const nextOptions = state.options.filter(o => o.id !== id);
  return {
    ...state,
    options: nextOptions,
    ...calculateCounts(nextOptions),
  };
};
